import { getAnalytics } from "@tm/insurance/analytics";
import { TextNodeType } from "@tm/isomorphic-utils";
import { FormattedText } from "@tm/ui-widgets/components";
import {
  IntersectionObserverData,
  useIntersectionObserver,
} from "@tm/ui/hooks";
import { useCallback, useRef } from "react";
import {
  OffersBenefitsSectionCardBadge,
  OffersBenefitsSectionCardBenefitImg,
  OffersBenefitsSectionCardBenefitInfo,
  OffersBenefitsSectionCardContainer,
  OffersBenefitsSectionCardDesc,
  OffersBenefitsSectionCards,
  OffersBenefitsSectionCardTitle,
  OffersBenefitsSectionContainer,
  OffersBenefitsSectionDescription,
  OffersBenefitsSectionTitle,
} from "./OffersBenefitsSection.style";

interface IOffersBenefitsSectionCard {
  title: TextNodeType;
  badge: {
    title: TextNodeType;
    bgGradient: string[];
  };
  image: string;
  description: TextNodeType;
  benefitInfo: TextNodeType;
}

export interface IOffersBenefitsSection {
  title: TextNodeType;
  description: TextNodeType;
  cards: IOffersBenefitsSectionCard[];
}

const OffersBenefitsSection = ({ data }: { data: IOffersBenefitsSection }) => {
  const { title, description, cards } = data;
  if (!data) return null;
  const analytics = getAnalytics();
  const hasTracked = useRef<boolean>(false);

  const handleIntersection = useCallback((data: IntersectionObserverData) => {
    if (!hasTracked.current) {
      analytics?.trackCommonLandingEvents({
        actionType: "offers_benefits_section_seen",
      });
    }
    hasTracked.current = true;
  }, []);

  const { ref: offersBenefitsSectionRef } = useIntersectionObserver(
    handleIntersection,
    {
      threshold: 0.5,
      triggerOnLeaveViewport: true,
    }
  );

  const OffersBenefitsSectionCard = ({
    key,
    card,
  }: {
    key: number;
    card: IOffersBenefitsSectionCard;
  }) => {
    const { title, badge, image, description, benefitInfo } = card;
    return (
      <OffersBenefitsSectionCardContainer key={key}>
        {badge && (
          <OffersBenefitsSectionCardBadge bgGradient={badge.bgGradient}>
            <FormattedText value={badge.title} />
          </OffersBenefitsSectionCardBadge>
        )}
        <OffersBenefitsSectionCardTitle>
          <FormattedText value={title} />
        </OffersBenefitsSectionCardTitle>
        <OffersBenefitsSectionCardDesc>
          <FormattedText value={description} />
        </OffersBenefitsSectionCardDesc>
        <OffersBenefitsSectionCardBenefitImg src={image} />
        <OffersBenefitsSectionCardBenefitInfo>
          <FormattedText value={benefitInfo} />
        </OffersBenefitsSectionCardBenefitInfo>
      </OffersBenefitsSectionCardContainer>
    );
  };

  return (
    <OffersBenefitsSectionContainer ref={offersBenefitsSectionRef}>
      <OffersBenefitsSectionTitle>
        <FormattedText value={title} />
      </OffersBenefitsSectionTitle>
      <OffersBenefitsSectionDescription>
        <FormattedText value={description} />
      </OffersBenefitsSectionDescription>
      <OffersBenefitsSectionCards>
        {cards.map((card, index) => (
          <OffersBenefitsSectionCard key={index} card={card} />
        ))}
      </OffersBenefitsSectionCards>
    </OffersBenefitsSectionContainer>
  );
};

export default OffersBenefitsSection;
